import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, IconButton, Tooltip, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const hashAlgorithms = ['SHA-1', 'SHA-256', 'SHA-512'];

const HashOutput = ({ algorithm, hash }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(hash);
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>{algorithm}</Typography>
      <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', backgroundColor: 'action.hover' }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1, wordBreak: 'break-all' }}>
          {hash || <span style={{ color: '#888' }}>...</span>}
        </Typography>
        <Tooltip title="Copy">
          <IconButton onClick={handleCopy} size="small" disabled={!hash}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </Box>
  );
};

function HashGenerator() {
  const [input, setInput] = useState('Hello World');
  const [hashes, setHashes] = useState({});

  useEffect(() => {
    const generateHashes = async () => {
      if (!input.trim()) {
        setHashes({});
        return;
      }

      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const newHashes = {};

      for (const alg of hashAlgorithms) {
        try {
          const hashBuffer = await crypto.subtle.digest(alg, data);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          newHashes[alg] = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } catch (e) {
          newHashes[alg] = `Error: ${e.message}`;
        }
      }
      setHashes(newHashes);
    };

    const timer = setTimeout(generateHashes, 300); // Debounce
    return () => clearTimeout(timer);
  }, [input]);

  return (
    <>
      <SEO
        title="Hash Generator | SHA-256, SHA-512 Online | UmbrellaCo"
        description="Generate cryptographic hashes securely in your browser. Supports SHA-1, SHA-256, and SHA-512. Free online developer utility."
        keywords="hash generator, sha256 generator, online hash tool, sha512 calculator, text to hash"
        url="/dev-tools/hash-generator"
        jsonLd={createSoftwareAppSchema(
          'Cryptographic Hash Generator',
          'Instantly generate SHA cryptographic hashes from text.',
          'https://umbrellaco.in/dev-tools/hash-generator',
          ['Cryptography', 'Hash Generation', 'Security Tools']
        )}
      />
      <ToolPageContainer
        title="Hash Generator"
        description="Securely convert text into various cryptographic hashes (SHA-1, SHA-256, SHA-512) locally in your browser."
      >
      
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <TextField
          label="Input Text"
          multiline
          rows={8}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          variant="outlined"
          fullWidth
          sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%' }, '& textarea': { height: '100% !important' } }}
        />
      </Box>

      <Stack spacing={2} sx={{ mt: 3 }}>
        {hashAlgorithms.map(alg => (
          <HashOutput key={alg} algorithm={alg} hash={hashes[alg]} />
        ))}
      </Stack>
      </ToolPageContainer>
    </>
  );
}

export default HashGenerator;
